import React, { useEffect, useState, useCallback, useRef } from "react";
import CustomerLayout from "../../../layouts/CustomerLayout";
import {
  MessageComposer,
  SenderIdSelector,
  SendingOptions,
} from "../../../components/quickSms";
import { toast } from "react-toastify";
import senderRequests from "../../../api/Customer/SenderRequests";
import contactRequests from "../../../api/Customer/ContactRequests";
import smsRequests from "../../../api/Customer/SmsRequests";
import subscriptionRequests from "../../../api/Customer/SubscriptionRequests";
import Loader from "../../../components/utilities/Loader";
import ScheduleCampaignModal from "./ScheduleCampaignModal";
import ExclusionDrawer from "../../../components/bulkSms/ExclusionDrawer";
import ExclusionsViewDrawer from "../../../components/bulkSms/ExclusionsViewDrawer";
import { motion, AnimatePresence } from "motion/react";
import { Calendar } from "lucide-react";
import {
  Users,
  MessageSquare,
  Upload,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  FileText,
  Phone,
  X,
  Send,
  Search,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  RefreshCw,
  RefreshCw as RefreshCwIcon,
  ShieldOff,
  Eye,
} from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { updateUserUnits, updateSubscriptionData } from "../../../redux/userSlice";
import { useLocation, useNavigate } from "react-router-dom";

const BulkSmsHeader = ({ selectedGroups, message = "", excludedCount = 0, userUnits }) => {
  const [availableUnits, setAvailableUnits] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasEnoughUnits, setHasEnoughUnits] = useState(false);

  // Fetch units from API
useEffect(() => {
  const fetchUnits = async () => {
    try {
      setIsLoading(true);
      const response = await subscriptionRequests.getCurrentUserSubscriptionData();

      const processed = response.processedResponse;

      if (processed?.status_code === "000") {
        const unitsFromAPI = processed.data?.units || 0;
        setAvailableUnits(parseInt(unitsFromAPI, 10) || 0);
      } else {
        setAvailableUnits(0);
      }
    } catch (error) {
      console.error("Error fetching subscription units:", error);
      setAvailableUnits(0);
    } finally {
      setIsLoading(false);
    }
  };

  fetchUnits();
}, []);

  // Keep in step with Redux units (updated after sends and on the parent's refresh)
  useEffect(() => {
    if (userUnits !== undefined && userUnits !== null) {
      setAvailableUnits(parseInt(userUnits, 10) || 0);
    }
  }, [userUnits]);

  // Calculate SMS costs and check if enough units
  useEffect(() => {
    const messageLength = message.length;
    const smsParts = Math.ceil(messageLength / 160) || 1;
    const rawContacts = selectedGroups.reduce(
      (sum, group) => sum + (group.number_of_contacts || 0),
      0
    );
    const effectiveContacts = Math.max(0, rawContacts - excludedCount);
    const totalSms = effectiveContacts * smsParts;

    setHasEnoughUnits(availableUnits >= totalSms);
  }, [selectedGroups, message, availableUnits, excludedCount]);

  const messageLength = message.length;
  const smsParts = Math.ceil(messageLength / 160) || 1;
  const rawContacts = selectedGroups.reduce(
    (sum, group) => sum + (group.number_of_contacts || 0),
    0
  );
  const totalContacts = Math.max(0, rawContacts - excludedCount);
  const totalSms = totalContacts * smsParts;

  if (isLoading) {
    return (
      <div className="p-4 mb-6 bg-white border border-gray-200 rounded-2xl shadow-sm animate-pulse md:p-6">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-5">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative p-4 mb-6 bg-white border border-red-100 rounded-2xl shadow-sm md:p-6 lg:p-8">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-red-50/50 blur-xl md:w-40 md:h-40"></div>
      </div>

      <div className="relative z-10">
        {/* Header section */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="space-y-2">
            <h1 className="text-xl font-bold text-gray-800 sm:text-2xl md:text-3xl lg:text-4xl">
              Bulk SMS Campaign
            </h1>
            <p className="text-sm text-gray-600 md:text-base">
              Send messages to multiple contact groups at once
            </p>
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg border border-red-100 md:px-4">
            <MessageSquare className="w-4 h-4 text-red-600 md:w-5" />
            <span className="text-sm font-medium text-red-700">Bulk Messaging</span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="p-3 mt-4 bg-white rounded-xl border border-gray-100 shadow-xs md:p-4 md:mt-6">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5 lg:gap-4">
            {/* Groups */}
            <div className="p-3 text-center bg-red-50/50 rounded-lg border border-red-100 md:p-4">
              <div className="text-lg font-bold text-red-600 md:text-xl lg:text-2xl">
                {selectedGroups.length}
              </div>
              <div className="text-xs text-gray-600 md:text-sm">Groups</div>
            </div>

            {/* Contacts */}
            <div className="p-3 text-center bg-blue-50/50 rounded-lg border border-blue-100 md:p-4">
              <div className="text-lg font-bold text-blue-600 md:text-xl lg:text-2xl">
                {totalContacts}
              </div>
              <div className="text-xs text-gray-600 md:text-sm">Contacts</div>
            </div>

            {/* Characters */}
            <div className="p-3 text-center bg-green-50/50 rounded-lg border border-green-100 md:p-4">
              <div className="text-lg font-bold text-green-600 md:text-xl lg:text-2xl">
                {messageLength}
              </div>
              <div className="text-xs text-gray-600 md:text-sm">Characters</div>
            </div>

            {/* Total SMS */}
            <div className="p-3 text-center bg-purple-50/50 rounded-lg border border-purple-100 md:p-4">
              <div className="text-lg font-bold text-purple-600 md:text-xl lg:text-2xl">
                {totalSms}
              </div>
              <div className="text-xs text-gray-600 md:text-sm">Total SMS</div>
            </div>

            {/* Units Left */}
            <div className={`p-3 text-center rounded-lg border md:p-4 ${
              hasEnoughUnits 
                ? "bg-emerald-50/50 border-emerald-100" 
                : "bg-amber-50/50 border-amber-100"
            }`}>
              <div className={`text-lg font-bold md:text-xl lg:text-2xl ${
                hasEnoughUnits ? "text-emerald-600" : "text-amber-600"
              }`}>
                {availableUnits}
              </div>
              <div className="text-xs text-gray-600 md:text-sm">Units Left</div>
            </div>
          </div>
        </div>

        {/* Unit Warning */}
        {/* {!hasEnoughUnits && selectedGroups.length > 0 && (
          <div className="p-3 mt-4 bg-white rounded-xl border border-amber-200 shadow-xs md:p-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-3 md:items-center">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5 md:mt-0" />
                <div className="space-y-1">
                  <div className="font-semibold text-amber-700 text-sm md:text-base">
                    Insufficient SMS Units for Campaign
                  </div>
                  <div className="text-xs text-amber-600 md:text-sm">
                    Need {totalSms} units but only have {availableUnits} units remaining.
                    {availableUnits > 0
                      ? " Consider reducing groups or message length."
                      : " Please purchase a subscription plan."}
                  </div>
                </div>
              </div>
              <button
                onClick={() => (window.location.href = "/subscription-plans")}
                className="px-3 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg shadow-sm transition-colors hover:bg-amber-700 w-full md:w-auto md:px-4"
              >
                Get More Units
              </button>
            </div>
          </div>
        )} */}

        {/* Refresh Units Button */}
        <div className="flex justify-end mt-4">
          <button
            onClick={async () => {
              setIsLoading(true);
              try {
                const response = await subscriptionRequests.getCurrentUserSubscriptionData();
                if ((response.status_code === "000" || response.status_code === "200") && response.data) {
                  const unitsFromAPI =
                    response.data.units ??
                    response.data.data?.units ??
                    response.data.remaining_units ??
                    response.data.sms_balance ??
                    0;
                  setAvailableUnits(parseInt(unitsFromAPI, 10) || 0);
                }
              } catch (error) {
                console.error("Error refreshing units:", error);
              } finally {
                setIsLoading(false);
              }
            }}
            className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200 md:text-sm md:px-4"
          >
            <RefreshCw className="w-3 h-3 md:w-4" />
            Refresh Units
          </button>
        </div>
      </div>
    </div>
  );
};

const ContactGroupSelector = ({
  selectedGroups,
  setSelectedGroups,
  contactGroups,
  isLoading,
  message = "",
  userUnits = 0,
  pagination,
  onPageChange,
  onPerPageChange,
  searchTerm,
  setSearchTerm,
  dateFrom,
  dateTo,
  onDateFilterChange,
  onClearDateFilters,
  excludedContacts = [],
  onOpenExclusionDrawer,
  onOpenExclusionsViewDrawer,
}) => {
  const availableUnits = parseInt(userUnits) || 0;
  const messageLength = message.length;
  const smsParts = Math.ceil(messageLength / 160) || 1;
  const [showAllGroups, setShowAllGroups] = useState(false);
  const navigate = useNavigate();

  // Filter groups based on showAllGroups toggle
  const filteredByEmpty = showAllGroups
    ? contactGroups
    : contactGroups.filter((group) => group.number_of_contacts > 0);

  // Filter groups based on search query
  const filteredBySearch = searchTerm.trim()
    ? filteredByEmpty.filter((group) =>
        (group.group_name || group.contact_group_name || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase().trim())
      )
    : filteredByEmpty;

  // Use contactGroups directly (already paginated by API)
  const displayGroups = filteredBySearch;

  const activeCount = contactGroups.filter(
    (group) => group.number_of_contacts > 0
  ).length;
  const emptyCount = contactGroups.filter(
    (group) => group.number_of_contacts === 0
  ).length;

  const getStatusIcon = (group) => {
    if (group.uploaded_at === null && group.number_of_contacts === null) {
      return (
        <div className="w-4 h-4 rounded-full border-2 border-yellow-500 animate-spin border-t-transparent"></div>
      );
    }
    return group.number_of_contacts > 0 ? (
      <CheckCircle className="w-4 h-4 text-red-500" />
    ) : (
      <AlertCircle className="w-4 h-4 text-gray-400" />
    );
  };

  const getStatusColor = (group) => {
    if (group.uploaded_at === null && group.number_of_contacts === null) {
      return "border-yellow-200 bg-yellow-50";
    }
    return group.number_of_contacts > 0
      ? "border-red-200 bg-red-50"
      : "border-gray-200 bg-gray-50";
  };

  const getStatusText = (group) => {
    if (group.uploaded_at === null && group.number_of_contacts === null) {
      return "Processing...";
    }
    return group.number_of_contacts > 0
      ? `${group.number_of_contacts} contacts`
      : "No contacts";
  };

  const isGroupSelected = (group) => {
    return selectedGroups.some((selected) => selected.id === group.id);
  };

  const canSelectGroup = (group) => {
    if (group.number_of_contacts === 0) return false;

    const groupSmsCost = group.number_of_contacts * smsParts;
    const currentSelectedSmsCost = selectedGroups.reduce(
      (sum, selected) => sum + selected.number_of_contacts * smsParts,
      0
    );
    const totalSmsCost = currentSelectedSmsCost + groupSmsCost;

    return availableUnits >= totalSmsCost;
  };

  const handleGroupToggle = (group) => {
    if (group.number_of_contacts === 0) return;

    if (isGroupSelected(group)) {
      setSelectedGroups(
        selectedGroups.filter((selected) => selected.id !== group.id)
      );
    } else {
      if (!canSelectGroup(group)) {
        const groupSmsCost = group.number_of_contacts * smsParts;
        toast.error(
          `Cannot add this group. It would require ${groupSmsCost} units, but you only have ${availableUnits} units remaining.`
        );
        return;
      }
      setSelectedGroups([...selectedGroups, group]);
    }
  };

  return (
    <div className="p-6 mb-6 bg-white rounded-2xl border border-red-100 shadow-xl">
      <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:justify-between sm:items-center">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-red-50 rounded-xl border border-red-200">
            <Users className="text-xl text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800 sm:text-xl">
              Contact Groups
            </h3>
            <p className="text-xs text-gray-600 sm:text-sm">
              Choose one or more groups to send messages to
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {/* Show Empty Groups Toggle */}
          {emptyCount > 0 && (
            <button
              onClick={() => setShowAllGroups(!showAllGroups)}
              className="flex justify-center items-center px-3 py-2 space-x-2 text-sm text-gray-600 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
            >
              <FileText className="text-sm" />
              <span>
                {showAllGroups ? "Hide Empty" : `Show Empty (${emptyCount})`}
              </span>
            </button>
          )}

          <button
            onClick={() =>
              navigate("/contacts/upload", {
                state: { locationRoute: window.location.href },
              })
            }
            className="flex justify-center items-center px-4 py-2 space-x-2 w-full text-white bg-gradient-to-r from-red-500 to-red-600 rounded-lg transition-all transform hover:from-red-600 hover:to-red-700 hover:scale-105 sm:w-auto"
          >
            <Upload className="text-lg" />
            <span className="text-sm sm:text-base">Upload Contacts</span>
          </button>

          {/* Exclusion Button — only shown when at least one group is selected */}
          {selectedGroups.length > 0 && (
            excludedContacts.length > 0 ? (
              <button
                onClick={onOpenExclusionsViewDrawer}
                className="flex justify-center items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors w-full sm:w-auto"
              >
                <Eye className="w-4 h-4" />
                {excludedContacts.length} Excluded
              </button>
            ) : (
              // <button
              //   onClick={onOpenExclusionDrawer}
              //   className="flex justify-center items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors w-full sm:w-auto"
              // >
              //   <ShieldOff className="w-4 h-4" />
              //   Exclude Numbers
              // </button>
              <></>
            )
          )}
        </div>
      </div>

      {/* Date Filters */}
      <div className="mb-4 flex flex-wrap gap-2">
        <div className="flex gap-2">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => onDateFilterChange(e.target.value, dateTo)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            placeholder="From"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => onDateFilterChange(dateFrom, e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            placeholder="To"
          />
          {(dateFrom || dateTo) && (
            <button
              onClick={onClearDateFilters}
              className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg border border-gray-300 transition-colors hover:bg-gray-200"
            >
              Clear Dates
            </button>
          )}
        </div>
      </div>

      {/* Search Bar */}
      {!isLoading && contactGroups.length > 0 && (
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search contact groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="py-3 pr-4 pl-10 w-full text-gray-700 bg-gray-50 rounded-lg border border-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 text-gray-400 transition-colors -translate-y-1/2 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {searchTerm && (
            <div className="mt-2 text-sm text-gray-600">
              Found {pagination.total} group{pagination.total !== 1 ? "s" : ""} matching "{searchTerm}"
            </div>
          )}
        </div>
      )}

      {/* Unit Summary */}
      {selectedGroups.length > 0 && (
        <div className="p-4 mb-4 bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-red-50 rounded-lg border border-red-100">
                <svg
                  className="w-4 h-4 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
              </div>
              <div>
                <div className="font-semibold text-gray-800">
                  Campaign Cost Summary
                </div>
                <div className="text-sm text-gray-600">
                  {selectedGroups.length} group(s) selected
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-red-600">
                {selectedGroups.reduce(
                  (sum, group) => sum + group.number_of_contacts * smsParts,
                  0
                )}{" "}
                units
              </div>
              <div className="text-xs text-gray-600">
                {availableUnits} available
              </div>
            </div>
          </div>

          {(() => {
            const totalCost = selectedGroups.reduce(
              (sum, group) => sum + group.number_of_contacts * smsParts,
              0
            );
            const hasEnough = availableUnits >= totalCost;

            if (!hasEnough) {
              return (
                <div className="p-3 mt-3 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center space-x-2">
                    <svg
                      className="w-4 h-4 text-red-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                    <span className="text-sm font-medium text-red-700">
                      Need {totalCost - availableUnits} more units to send this
                      campaign
                    </span>
                  </div>
                </div>
              );
            }
            return (
              <div className="p-3 mt-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-2">
                  <svg
                    className="w-4 h-4 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">
                    Sufficient units available ({availableUnits - totalCost}{" "}
                    remaining)
                  </span>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 rounded-full border-2 border-blue-500 animate-spin border-t-transparent"></div>
            <span className="text-gray-600">Loading contact groups...</span>
          </div>
        </div>
      )}

      {/* Groups Grid */}
      {!isLoading && (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {contactGroups.length === 0 ? (
            <div className="col-span-full py-8 text-center">
              <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full">
                <Users className="text-2xl text-gray-400" />
              </div>
              <h4 className="mb-2 text-lg font-semibold text-gray-700">
                No Contact Groups Available
              </h4>
              <p className="mb-4 text-gray-500">
                You need to upload contacts to create groups for bulk messaging
              </p>
              <button
                onClick={() => navigate("/contacts/upload")}
                className="px-4 py-2 text-white bg-gradient-to-r from-red-500 to-red-600 rounded-lg transition-all hover:from-red-600 hover:to-red-700"
              >
                Upload Your First Contacts
              </button>
            </div>
          ) : displayGroups.length === 0 && !searchTerm ? (
            <div className="col-span-full py-8 text-center">
              <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full">
                <Users className="text-2xl text-gray-400" />
              </div>
              <h4 className="mb-2 text-lg font-semibold text-gray-700">
                No Active Groups
              </h4>
              <p className="mb-4 text-gray-500">
                {showAllGroups
                  ? "No contact groups available"
                  : "All your contact groups are empty. Upload contacts to get started."}
              </p>
              {!showAllGroups && (
                <button
                  onClick={() => setShowAllGroups(true)}
                  className="px-4 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg transition-colors hover:bg-blue-100"
                >
                  Show Empty Groups
                </button>
              )}
            </div>
          ) : (
            displayGroups.map((group) => (
              <div
                key={group.id}
                onClick={() => handleGroupToggle(group)}
                className={`relative p-2 rounded-lg border cursor-pointer transition-all ${
                  isGroupSelected(group)
                    ? "border-red-400 bg-red-50 shadow-md"
                    : group.number_of_contacts > 0 && canSelectGroup(group)
                    ? "border-gray-200 bg-white hover:border-red-300 hover:shadow-sm"
                    : group.number_of_contacts > 0 && !canSelectGroup(group)
                    ? "border-red-200 bg-white opacity-75"
                    : getStatusColor(group)
                } ${
                  group.number_of_contacts === 0 ||
                  (group.number_of_contacts > 0 && !canSelectGroup(group))
                    ? "cursor-not-allowed opacity-75"
                    : ""
                }`}
              >
                {/* Selection indicator */}
                {isGroupSelected(group) && (
                  <div className="absolute -top-1 -right-1 p-0.5 text-white bg-red-600 rounded-full">
                    <CheckCircle className="w-3 h-3" />
                  </div>
                )}

                <div className="flex justify-between items-center mb-1">
                  <div className="flex items-center space-x-1.5 flex-1 min-w-0">
                    <div className="flex-shrink-0 p-1 bg-white rounded border border-gray-100">
                      <Users className="w-3.5 h-3.5 text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-800 truncate text-md">
                        {group.group_name}
                      </h4>
                    </div>
                  </div>
                </div>

                <div className="text-[10px] text-gray-600 mb-1">
                  <span className="font-medium">Status:</span>{" "}
                  {getStatusText(group)}
                </div>

                {group.uploaded_at === null &&
                  group.number_of_contacts === null && (
                    <div className="p-1 mt-1 text-[10px] text-yellow-600 bg-yellow-50 rounded border border-yellow-200">
                      ⏱️ Processing...
                    </div>
                  )}

                {group.number_of_contacts === 0 &&
                  group.uploaded_at !== null && (
                    <div className="p-1 mt-1 text-[10px] text-gray-600 bg-gray-50 rounded border border-gray-200">
                      📝 Empty
                    </div>
                  )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Pagination Controls */}
      {!isLoading && pagination.total > pagination.perPage && (
        <div className="flex flex-col gap-4 mt-6 sm:flex-row sm:justify-between sm:items-center">
          <div className="text-sm text-gray-600">
            Showing {pagination.from} to {pagination.to} of {pagination.total} group{pagination.total !== 1 ? "s" : ""}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex gap-2 items-center">
              <button
                onClick={() => onPageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  pagination.currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <div className="flex gap-1 items-center">
                {Array.from({ length: Math.min(pagination.lastPage, 7) }, (_, i) => {
                  let pageNum;
                  if (pagination.lastPage <= 7) {
                    pageNum = i + 1;
                  } else if (pagination.currentPage <= 4) {
                    pageNum = i + 1;
                  } else if (pagination.currentPage >= pagination.lastPage - 3) {
                    pageNum = pagination.lastPage - 6 + i;
                  } else {
                    pageNum = pagination.currentPage - 3 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => onPageChange(pageNum)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                        pagination.currentPage === pageNum
                          ? "bg-red-600 text-white"
                          : "bg-white text-gray-700 border border-gray-300 hover:bg-red-50 hover:border-red-300"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => onPageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.lastPage}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  pagination.currentPage === pagination.lastPage
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                }`}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            
            {/* Per page selector */}
            <select
              value={pagination.perPage}
              onChange={(e) => onPerPageChange(parseInt(e.target.value))}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value={8}>8 per page</option>
              <option value={15}>15 per page</option>
              <option value={30}>30 per page</option>
              <option value={50}>50 per page</option>
            </select>
          </div>
        </div>
      )}

      {/* No Results Message */}
      {!isLoading && searchTerm && filteredBySearch.length === 0 && (
        <div className="py-8 text-center">
          <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full">
            <Search className="text-2xl text-gray-400" />
          </div>
          <h4 className="mb-2 text-lg font-semibold text-gray-700">
            No Groups Found
          </h4>
          <p className="mb-4 text-gray-500">
            No contact groups match "{searchTerm}". Try a different search term.
          </p>
          <button
            onClick={() => setSearchTerm("")}
            className="px-4 py-2 text-sm text-red-600 bg-red-50 rounded-lg transition-colors hover:bg-red-100"
          >
            Clear Search
          </button>
        </div>
      )}
    </div>
  );
};

const BulkSms = () => {
  // State management
  
const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [message, setMessage] = useState("");
  const [selectedSenderId, setSelectedSenderId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSenders, setIsLoadingSenders] = useState(true);
  const [isLoadingGroups, setIsLoadingGroups] = useState(true);
  const [, setSenderIds] = useState([]);
  const [allSenderIds, setAllSenderIds] = useState([]);
  const [contactGroups, setContactGroups] = useState([]);
  const [showFloatingSend, setShowFloatingSend] = useState(false);
  const [showRoadmap, setShowRoadmap] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [userUnits, setUserUnits] = useState(0);
  const [subscriptionData, setSubscriptionData] = useState(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 8,
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0,
  });
  
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Exclusion state
  const [excludedContacts, setExcludedContacts] = useState([]);
  const [showExclusionDrawer, setShowExclusionDrawer] = useState(false);
  const [showExclusionsViewDrawer, setShowExclusionsViewDrawer] = useState(false);

  const user = useSelector((state) => state.user?.txtconnect_ng_user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const subscriptionUnitsLeft = user?.units;
  const [hasSubscription, setHasSubscription] = useState(subscriptionUnitsLeft > 0);

  // Sync userUnits with Redux
  useEffect(() => {
    if (subscriptionUnitsLeft !== undefined && subscriptionUnitsLeft !== userUnits) {
      setUserUnits(subscriptionUnitsLeft);
      setHasSubscription(subscriptionUnitsLeft > 0);
    }
  }, [subscriptionUnitsLeft, userUnits]);

  // Restore exclusions from sessionStorage on mount
  useEffect(() => {
    const stored = sessionStorage.getItem("bulk_sms_excluded_contacts");
    if (stored) {
      try {
        setExcludedContacts(JSON.parse(stored));
      } catch {
        // ignore malformed data
      }
    }
  }, []);

  // Handler: update excluded contacts and sync sessionStorage
  const handleExclusionsUpdate = (updated) => {
    setExcludedContacts(updated);
    if (updated.length > 0) {
      sessionStorage.setItem("bulk_sms_excluded_contacts", JSON.stringify(updated));
    } else {
      sessionStorage.removeItem("bulk_sms_excluded_contacts");
    }
  };

  // Debounced search term for API calls
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  // Handle page change
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  // Handle per page change
  const handlePerPageChange = (newPerPage) => {
    setPagination(prev => ({ ...prev, perPage: newPerPage, currentPage: 1 }));
  };

  // Handle date filter change
  const handleDateFilterChange = (from, to) => {
    setDateFrom(from || "");
    setDateTo(to || "");
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleClearDateFilters = () => {
    setDateFrom("");
    setDateTo("");
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // Show roadmap for first-time users
  useEffect(() => {
    const hasSeenRoadmap = localStorage.getItem("bulkSmsRoadmapSeen");
    if (!hasSeenRoadmap && userUnits > 0) {
      const timer = setTimeout(() => {
        setShowRoadmap(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [userUnits]);

  // Handle scroll to show/hide floating send button
  useEffect(() => {
    const handleScroll = () => {
      const messageComposer = document.getElementById("bulk-message-composer");
      if (messageComposer) {
        const rect = messageComposer.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
        const hasContent =
          selectedGroups.length > 0 && message.trim().length > 0;

        setShowFloatingSend(isVisible && hasContent);

        const sidebarSendButton = document.getElementById(
          "sidebar-send-button"
        );
        if (sidebarSendButton) {
          if (isVisible && hasContent) {
            sidebarSendButton.classList.add(
              "animate-pulse",
              "ring-2",
              "ring-blue-500",
              "ring-opacity-50"
            );
          } else {
            sidebarSendButton.classList.remove(
              "animate-pulse",
              "ring-2",
              "ring-blue-500",
              "ring-opacity-50"
            );
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [selectedGroups.length, message]);

  // Fetch contact groups with pagination
  useEffect(() => {
    const fetchContactGroups = async () => {
      if (!hasSubscription) return;
      
      setIsLoadingGroups(true);
      try {
        const response = await contactRequests.getContactGroups(
          pagination.currentPage,
          pagination.perPage,
          debouncedSearchTerm,
          "", // sortBy
          "asc", // sortOrder
          dateFrom,
          dateTo
        );
        
        const { data } = response;
        
        if (data?.data?.status_code === "000" && !data?.data?.in_error) {
          const groups = data.data.data?.contact_groups || [];
          const paginationData = data.data.data?.pagination || {};
          
          setContactGroups(groups);
          setPagination({
            currentPage: paginationData.current_page || pagination.currentPage,
            perPage: paginationData.per_page || pagination.perPage,
            total: paginationData.total || 0,
            lastPage: paginationData.last_page || 1,
            from: paginationData.from || 0,
            to: paginationData.to || 0,
          });
        } else {
          toast.error(data?.data?.reason || "Error fetching contact groups");
        }
      } catch (error) {
        toast.error("Error fetching contact groups");
        console.error("Error fetching contact groups:", error);
      } finally {
        setIsLoadingGroups(false);
      }
    };

    fetchContactGroups();
  }, [pagination.currentPage, pagination.perPage, debouncedSearchTerm, dateFrom, dateTo, hasSubscription]);

  // Fetch sender IDs
  useEffect(() => {
    const fetchSenderIds = async () => {
      if (!hasSubscription) return;
      
      setIsLoadingSenders(true);
      try {
        // Use pagination format: page 1, high limit to get all sender IDs, no search
        const senderResponse = await senderRequests.getSenders(1, 100, "");
        const { data: senderData } = senderResponse;

        if (
          senderData?.data?.status_code === "000" &&
          !senderData?.data?.in_error
        ) {
          // For dropdown selectors, we want all sender IDs, not paginated results
          const allSenders = senderData?.data?.data?.data || senderData?.data?.data || [];
          const approvedSenderIds = allSenders.filter(
            (sender) => sender.status.toLowerCase() === "approved"
          );

          setAllSenderIds(allSenders);
          setSenderIds(approvedSenderIds);

          // if (approvedSenderIds && approvedSenderIds.length > 0) {
          //   setSelectedSenderId(approvedSenderIds[0].sender);
          // }
        } else {
          toast.error(
            senderData?.data?.data?.reason || "Error fetching sender ids"
          );
        }
      } catch (error) {
        toast.error("Error fetching sender IDs");
        console.error("Fetch sender error:", error);
      } finally {
        setIsLoadingSenders(false);
      }
    };

    if (hasSubscription) {
      fetchSenderIds();
    }
  }, [hasSubscription]);

  // Fetch subscription data and sync units into state + Redux (and so localStorage)
  const refreshUnits = useCallback(async () => {
    try {
      const response = await subscriptionRequests.getCurrentUserSubscriptionData();

      if ((response?.status_code === "000" || response?.status_code === "200") && response?.data) {
        // response.data is the user/subscription record (plan_name, units, subscription_plan)
        const subscription = response.data;
        const units =
          subscription?.units !== undefined
            ? parseInt(subscription.units, 10) || 0
            : subscription?.subscription_plan?.units !== undefined
              ? parseInt(subscription.subscription_plan.units, 10) || 0
              : 0;

        setUserUnits(units);
        setHasSubscription(units > 0);
        setSubscriptionData(subscription);
        dispatch(updateUserUnits({ units }));
        dispatch(
          updateSubscriptionData({
            subscriptionData: subscription?.subscription_plan,
            plan_name: subscription?.plan_name,
            units,
          })
        );
      }
    } catch (error) {
      console.error("Error fetching subscription data:", error);
    }
  }, [dispatch]);

  useEffect(() => {
    refreshUnits();

    // Set up interval to refresh subscription data every 30 seconds
    const intervalId = setInterval(refreshUnits, 30000);

    // Also refresh when component becomes visible (page focus)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshUnits();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshUnits]);

  // Follow-up unit checks after a bulk send, since the backend may deduct units after queueing
  const postSendTimers = useRef([]);
  useEffect(() => () => postSendTimers.current.forEach(clearTimeout), []);

  // Handle adding new sender ID
  const handleAddSender = async (senderName) => {
    try {
      const response = await senderRequests.addSenderId(senderName);
      const data = response?.data
      const validationError =  response?.validationError || null;
      if(validationError) {
        toast.error(validationError);
        return;
      }

      if (data?.data?.status_code === "000" && !data?.data?.in_error) {
        // Refresh sender IDs using pagination format
        const refreshResponse = await senderRequests.getSenders(1, 100, "");
        const { data: refreshData } = refreshResponse;

        if (
          refreshData?.data?.status_code === "000" &&
          !refreshData?.data?.in_error
        ) {
          const allSenders = refreshData?.data?.data?.data || refreshData?.data?.data || [];
          const approvedSenderIds = allSenders.filter(
            (sender) => sender.status.toLowerCase() === "approved"
          );

          setAllSenderIds(allSenders);
          setSenderIds(approvedSenderIds);
        }

        toast.success("Sender ID submitted for approval!");
        return true;
      } else {
        toast.error(data?.data?.reason || "Failed to add sender ID");
        return false;
      }
    } catch (error) {
      toast.error("An error occurred while adding the sender ID");
      console.error("Add sender error:", error);
      return false;
    }
  };

  // Handle message sending
  const handleSend = async (sendData) => {
    if (!selectedSenderId) {
      toast.error("Please select a sender ID");
      return;
    }

    if (selectedGroups.length === 0) {
      toast.error("Please select at least one contact group");
      return;
    }

    if (!sendData.message.trim()) {
      toast.error("Please enter a message");
      return;
    }

    // Check if user has sufficient units (excluding excluded contacts)
    const smsPartLength = 160;
    const messageLength = sendData.message.length;
    const smsParts = Math.ceil(messageLength / smsPartLength) || 1;
    const rawContacts = selectedGroups.reduce(
      (sum, group) => sum + (group.number_of_contacts || 0),
      0
    );
    const effectiveContacts = Math.max(0, rawContacts - excludedContacts.length);
    const totalSms = effectiveContacts * smsParts;

    if (userUnits < totalSms) {
      toast.error(
        `Insufficient SMS units for this campaign. You need ${totalSms} units but only have ${userUnits} units remaining.`
      );
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        contact_group_slugs: selectedGroups.map(
          (group) => group.contact_group_slug
        ),
        sender_id: selectedSenderId,
        content: sendData.message,
        excluded_contacts: excludedContacts.map((e) => e.phone_number),
      };

      const response = await smsRequests.sendBulkSms(payload);

      if (response.success) {
        const sentCount = effectiveContacts;
        toast.success(
          `Bulk message sent successfully to ${sentCount} contacts across ${selectedGroups.length} groups!`
        );

        // Reset form and clear exclusions
        setSelectedGroups([]);
        setMessage("");
        setExcludedContacts([]);
        sessionStorage.removeItem("bulk_sms_excluded_contacts");

        // Pull fresh units now, then re-check while the backend deducts them
        refreshUnits();
        postSendTimers.current.forEach(clearTimeout);
        postSendTimers.current = [3000, 8000, 15000].map((delay) =>
          setTimeout(refreshUnits, delay)
        );
      } else {
        const errorMessage =
          response.error?.data?.reason ||
          response.error?.message ||
          "Failed to send bulk message";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error sending bulk message:", error);
      toast.error("Error sending bulk message. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Can send only when both contact group(s) and sender ID are selected
  const canSendBulk =
    selectedGroups.length > 0 && !!selectedSenderId;
  const sendDisabledMessage = !selectedSenderId
    ? "Select a sender ID to send your campaign."
    : selectedGroups.length === 0
    ? "Select at least one contact group to send your campaign."
    : "";

  // Floating send button handler
  const handleFloatingSend = () => {
    if (!canSendBulk) {
      toast.info(sendDisabledMessage || "Select at least one contact group and a sender ID to send.");
      return;
    }
    const sendData = {
      recipients: selectedGroups.map((group) => group.contact_group_slug),
      message,
      totalSms: (() => {
        const smsPartLength = 160;
        const messageLength = message.length;
        const smsParts = Math.ceil(messageLength / smsPartLength) || 1;
        const totalContacts = selectedGroups.reduce(
          (sum, group) => sum + (group.number_of_contacts || 0),
          0
        );
        return totalContacts * smsParts;
      })(),
    };
    handleSend(sendData);
  };

  // Roadmap handlers
  const handleSkipRoadmap = () => {
    setShowRoadmap(false);
    localStorage.setItem("bulkSmsRoadmapSeen", "true");
  };

  const handleNextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowRoadmap(false);
      localStorage.setItem("bulkSmsRoadmapSeen", "true");
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getStepContent = () => {
    switch (currentStep) {
      case 1:
        return {
          title: "Welcome to Bulk SMS!",
          description:
            "Let's get you started with sending bulk SMS campaigns. This guide will walk you through the process of sending messages to multiple contact groups.",
          icon: "📢",
          highlight: null,
        };
      case 2:
        return {
          title: "Step 1: Select Contact Groups",
          description:
            "Choose one or more contact groups to send your message to. You can see the number of contacts in each group and the SMS cost.",
          icon: "👥",
          highlight: "contact-groups",
        };
      case 3:
        return {
          title: "Step 2: Choose Sender ID",
          description:
            "Select how your recipients will see your messages. Make sure your sender ID is approved before sending.",
          icon: "🏷️",
          highlight: "sender-selector",
        };
      case 4:
        return {
          title: "Step 3: Write Your Campaign Message",
          description:
            "Compose your message in the text area. Keep it under 160 characters to avoid multiple SMS charges per recipient.",
          icon: "✍️",
          highlight: "message-composer",
        };
      default:
        return {
          title: "Ready to Send Campaign!",
          description:
            "Once you've completed all steps, use the send button on the right sidebar or the floating button that appears when you're ready to send.",
          icon: "🚀",
          highlight: "sidebar-send-button",
        };
    }
  };

  // Check if user has any units
  if (!hasSubscription || subscriptionUnitsLeft <= 0) {
    return (
      <CustomerLayout>
        <div className="px-4 pt-20 min-h-screen bg-white lg:pt-20 md:p-6">
          <div className="mx-auto max-w-4xl">
            <div className="p-8 text-center bg-white rounded-2xl border border-gray-200 shadow-xl">
              <div className="mb-6">
                <div className="flex justify-center items-center mx-auto mb-4 w-20 h-20 bg-red-50 rounded-full border border-red-100">
                  <svg
                    className="w-10 h-10 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <h2 className="mb-2 text-2xl font-bold text-gray-800">
                  No SMS Units Available
                </h2>
                <p className="mb-6 text-gray-600">
                  You need to purchase a subscription plan to send bulk SMS
                  campaigns.
                </p>
                
                {/* Show current subscription status if available */}
                {subscriptionData && (
                  <div className="p-4 mb-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700">
                      Current subscription:{" "}
                      <span className="font-semibold">
                        {subscriptionData.subscription_plan?.name || "No active plan"}
                      </span>
                    </p>
                    <p className="text-sm text-gray-700">
                      Units: <span className="font-semibold text-red-600">{userUnits}</span>
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <button
                  onClick={() => navigate("/subscription-plans")}
                  className="px-6 py-3 font-semibold text-white bg-red-600 rounded-xl shadow-md transition-all transform hover:bg-red-700 hover:scale-105"
                >
                  View Subscription Plans
                </button>

                <div className="text-sm text-gray-500">
                  <p>
                    Current balance:{" "}
                    <span className="font-semibold text-red-600">{userUnits} units</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CustomerLayout>
    );
  }

  if (isLoadingSenders || isLoadingGroups) {
    return <Loader />;
  }

  return (
    <CustomerLayout>
      <div className="px-4 pt-20 min-h-screen bg-white lg:pt-20 md:p-6">
        <div className="mx-auto max-w-8xl">
          {/* Header */}
          <BulkSmsHeader selectedGroups={selectedGroups} message={message} userUnits={subscriptionUnitsLeft} excludedCount={excludedContacts.length} />

          {/* Unit Balance Alert */}
          {(() => {
            const smsPartLength = 160;
            const messageLength = message.length;
            const smsParts = Math.ceil(messageLength / smsPartLength) || 1;
            const rawContacts = selectedGroups.reduce(
              (sum, group) => sum + (group.number_of_contacts || 0),
              0
            );
            const totalContacts = Math.max(0, rawContacts - excludedContacts.length);
            const totalSms = totalContacts * smsParts;
            const hasEnoughUnits = userUnits >= totalSms;

            if (selectedGroups.length > 0 && !hasEnoughUnits) {
              return (
                <div className="p-4 mb-6 bg-white rounded-xl border border-red-200 shadow-sm">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-red-50 rounded-lg border border-red-100">
                        <svg
                          className="w-5 h-5 text-red-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                          />
                        </svg>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-800">
                          Insufficient Units for Current Campaign
                        </div>
                        <div className="text-sm text-gray-600">
                          Need {totalSms} units, have {userUnits} units
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate("/subscription-plans")}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg shadow-md transition-colors hover:bg-red-700"
                    >
                      Get More Units
                    </button>
                  </div>
                </div>
              );
            }
            return null;
          })()}

          {/* Subscription Info Banner */}
          {subscriptionData && (
            <div className="p-4 mb-6 bg-gradient-to-r from-green-50 to-emerald-100 rounded-xl border border-green-200 shadow-sm">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-100 rounded-lg border border-green-200">
                    <svg
                      className="w-5 h-5 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800">
                      Active Subscription: {subscriptionData.subscription_plan?.name || "Plan"}
                    </div>
                    <div className="text-sm text-gray-600">
                      {userUnits} units available • 
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  {/* <div className="text-sm font-medium text-green-700">
                    Valid until renewal
                  </div>
                  <div className="text-xs text-gray-600">
                    {subscriptionData.plan_name || "1 month plan"}
                  </div> */}
                </div>
              </div>
            </div>
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <div className="space-y-6 xl:col-span-2">
              <div id="contact-groups">
                <ContactGroupSelector
                  selectedGroups={selectedGroups}
                  setSelectedGroups={setSelectedGroups}
                  contactGroups={contactGroups}
                  isLoading={isLoadingGroups}
                  message={message}
                  userUnits={userUnits}
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  onPerPageChange={handlePerPageChange}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  onDateFilterChange={handleDateFilterChange}
                  onClearDateFilters={handleClearDateFilters}
                  excludedContacts={excludedContacts}
                  onOpenExclusionDrawer={() => setShowExclusionDrawer(true)}
                  onOpenExclusionsViewDrawer={() => setShowExclusionsViewDrawer(true)}
                />
              </div>
              <div id="sender-selector">
                <SenderIdSelector
                  senderIds={allSenderIds}
                  selectedSenderId={selectedSenderId}
                  setSelectedSenderId={setSelectedSenderId}
                  onAddSender={handleAddSender}
                  isLoading={isLoadingSenders}
                />
              </div>
              <div id="message-composer">
                <MessageComposer message={message} setMessage={setMessage} showVariables={true} />
              </div>
            </div>

            <div className="xl:col-span-1">
              <div className="sticky top-6">
                <SendingOptions
                  recipients={selectedGroups.map(
                    (group) => group.contact_group_slug
                  )}
                  recipientCount={Math.max(
                    0,
                    selectedGroups.reduce(
                      (total, group) => total + (group.number_of_contacts || 0),
                      0
                    ) - excludedContacts.length
                  )}
                  message={message}
                  onSend={handleSend}
                  isLoading={isLoading}
                  sendDisabled={!canSendBulk}
                  sendDisabledMessage={sendDisabledMessage}
                  showScheduleButton={true}
                   onScheduleClick={() => setShowScheduleModal(true)}
                />
                
{/* <motion.button
  onClick={() => {
    if (!canSendBulk) {
      toast.info(sendDisabledMessage || "Select at least one contact group and a sender ID.");
      return;
    }
    setShowScheduleModal(true);
  }}
  disabled={!canSendBulk}
  whileHover={canSendBulk ? { scale: 1.02 } : {}}
  whileTap={canSendBulk ? { scale: 0.97 } : {}}
  className={`flex  mt-[1rem] items-center justify-center gap-2 w-full px-4 py-3 text-sm font-semibold rounded-xl border transition-all ${
    canSendBulk
      ? "border-red-200 text-red-600 bg-red-50 hover:bg-red-100 hover:border-red-300"
      : "border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed"
  }`}
>
  <Calendar className="w-4 h-4" />
  Schedule Campaign
</motion.button> */}

                {/* Navigation Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="p-6 mt-6 bg-gradient-to-r from-blue-50 to-indigo-100 rounded-2xl border border-blue-200 shadow-xl"
                >
                  <h4 className="flex items-center mb-4 space-x-2 font-bold text-blue-800">
                    <MessageSquare className="w-5 h-5" />
                    <span>Quick Actions</span>
                  </h4>
                  <div className="space-y-3">
                    <button
                      onClick={() => navigate("/quick-sms")}
                      className="flex justify-between items-center p-3 w-full bg-white rounded-xl border border-blue-200 transition-all hover:border-blue-300 hover:shadow-md"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Phone className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-gray-800">Quick SMS</p>
                          <p className="text-xs text-gray-600">
                            Send to individual numbers
                          </p>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-blue-600" />
                    </button>

                    <button
                      onClick={() => navigate("/contacts/upload", {
                        state: { locationRoute: location.pathname }
                      })}
                      className="flex justify-between items-center p-3 w-full bg-white rounded-xl border border-blue-200 transition-all hover:border-blue-300 hover:shadow-md"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-red-100 rounded-lg">
                          <Upload className="w-4 h-4 text-red-600" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-gray-800">
                            Upload Contacts
                          </p>
                          <p className="text-xs text-gray-600">
                            Add new contact groups
                          </p>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-blue-600" />
                    </button>
                    
                    <button
                      onClick={() => navigate("/subscription-plans")}
                      className="flex justify-between items-center p-3 w-full bg-white rounded-xl border border-green-200 transition-all hover:border-green-300 hover:shadow-md"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <svg
                            className="w-4 h-4 text-green-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-gray-800">
                            Buy More Units
                          </p>
                          <p className="text-xs text-gray-600">
                            Current: {userUnits} units
                          </p>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-green-600" />
                    </button>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* First-time User Roadmap */}
      <AnimatePresence>
        {showRoadmap && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex fixed inset-0 z-50 justify-center items-center backdrop-blur-sm bg-black/50"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="overflow-hidden relative mx-4 w-full max-w-md bg-white rounded-2xl shadow-2xl"
            >
              {/* Progress Bar */}
              <div className="w-full h-1 bg-gray-200">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(currentStep / 4) * 100}%` }}
                  className="h-full bg-gradient-to-r from-blue-500 to-purple-600"
                />
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="mb-6 text-center">
                  <div className="mb-4 text-4xl">{getStepContent().icon}</div>
                  <h3 className="mb-2 text-xl font-bold text-gray-800">
                    {getStepContent().title}
                  </h3>
                  <p className="leading-relaxed text-gray-600">
                    {getStepContent().description}
                  </p>
                </div>

                {/* Step Indicators */}
                <div className="flex justify-center mb-6 space-x-2">
                  {[1, 2, 3, 4].map((step) => (
                    <div
                      key={step}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        step <= currentStep
                          ? "bg-gradient-to-r from-blue-500 to-purple-600"
                          : "bg-gray-300"
                      }`}
                    />
                  ))}
                </div>

                {/* Navigation Buttons */}
                <div className="flex justify-between items-center">
                  <button
                    onClick={handleSkipRoadmap}
                    className="px-4 py-2 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700"
                  >
                    Skip Tutorial
                  </button>

                  <div className="flex space-x-3">
                    {currentStep > 1 && (
                      <button
                        onClick={handlePrevStep}
                        className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
                      >
                        Previous
                      </button>
                    )}
                    <button
                      onClick={handleNextStep}
                      className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg transition-all transform hover:from-blue-600 hover:to-purple-700 hover:scale-105"
                    >
                      {currentStep === 4 ? "Finish" : "Next"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Highlight overlay for current step */}
              {getStepContent().highlight && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.1) 70%, rgba(0,0,0,0.3) 100%)",
                  }}
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Send Button - Mobile */}
      <AnimatePresence>
        {showFloatingSend && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed right-6 bottom-6 z-50 lg:hidden"
          >
            <motion.button
              onClick={handleFloatingSend}
              disabled={isLoading || !canSendBulk}
              className={`flex items-center justify-center w-16 h-16 rounded-full shadow-2xl transition-all transform hover:scale-110 active:scale-95 ${
                isLoading || !canSendBulk
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              }`}
              whileHover={canSendBulk && !isLoading ? { scale: 1.1 } : {}}
              whileTap={canSendBulk && !isLoading ? { scale: 0.95 } : {}}
              animate={{
                boxShadow: isLoading || !canSendBulk
                  ? "0 10px 25px rgba(0,0,0,0.2)"
                  : "0 10px 25px rgba(59, 130, 246, 0.3)",
              }}
            >
              {isLoading ? (
                <div className="w-6 h-6 rounded-full border-2 border-white animate-spin border-t-transparent"></div>
              ) : (
                <Send className="w-6 h-6 text-white" />
              )}
            </motion.button>

            {/* Tooltip */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className={`absolute right-0 bottom-full px-3 py-2 mb-2 text-sm whitespace-nowrap rounded-lg shadow-lg max-w-[220px] ${
                canSendBulk
                  ? "text-white bg-gray-900"
                  : "text-gray-700 bg-amber-50 border border-amber-200"
              }`}
            >
              {canSendBulk ? "Send Campaign" : sendDisabledMessage || "Select contact group(s) and sender ID"}
              <div className={`absolute right-4 top-full w-0 h-0 border-t-4 border-r-4 border-l-4 border-transparent ${
                canSendBulk ? "border-t-gray-900" : "border-t-amber-50"
              }`}></div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop Send Button - Appears when sidebar is not visible */}
      <AnimatePresence>
        {showFloatingSend && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="hidden fixed right-8 bottom-8 z-50 lg:block"
          >
            <motion.button
              onClick={handleFloatingSend}
              disabled={isLoading || !canSendBulk}
              className={`flex items-center justify-center px-6 py-4 rounded-2xl shadow-2xl transition-all transform hover:scale-105 active:scale-95 ${
                isLoading || !canSendBulk
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              }`}
              whileHover={canSendBulk && !isLoading ? { scale: 1.05 } : {}}
              whileTap={canSendBulk && !isLoading ? { scale: 0.95 } : {}}
              animate={{
                boxShadow: isLoading || !canSendBulk
                  ? "0 10px 25px rgba(0,0,0,0.2)"
                  : "0 10px 25px rgba(59, 130, 246, 0.3)",
              }}
            >
              {isLoading ? (
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 rounded-full border-2 border-white animate-spin border-t-transparent"></div>
                  <span className="font-semibold text-white">Sending...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <Send className="w-5 h-5 text-white" />
                  <span className="font-semibold text-white">
                    Send Campaign
                  </span>
                </div>
              )}
            </motion.button>

            {/* Desktop Tooltip */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className={`absolute right-0 bottom-full px-4 py-2 mb-2 text-sm whitespace-nowrap rounded-lg shadow-lg max-w-[260px] ${
                canSendBulk
                  ? "text-white bg-gray-900"
                  : "text-gray-700 bg-amber-50 border border-amber-200"
              }`}
            >
              {canSendBulk ? "Send your bulk SMS campaign" : sendDisabledMessage || "Select at least one contact group and a sender ID to send."}
              <div className={`absolute right-4 top-full w-0 h-0 border-t-4 border-r-4 border-l-4 border-transparent ${
                canSendBulk ? "border-t-gray-900" : "border-t-amber-50"
              }`}></div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
<ScheduleCampaignModal
  isOpen={showScheduleModal}
  onClose={() => setShowScheduleModal(false)}
  selectedGroups={selectedGroups}
  selectedSenderId={selectedSenderId}
  message={message}
/>

<ExclusionDrawer
  isOpen={showExclusionDrawer}
  onClose={() => setShowExclusionDrawer(false)}
  selectedGroups={selectedGroups}
  onExclusionsUpdate={handleExclusionsUpdate}
/>

<ExclusionsViewDrawer
  isOpen={showExclusionsViewDrawer}
  onClose={() => setShowExclusionsViewDrawer(false)}
  onExclusionsUpdate={handleExclusionsUpdate}
/>
    </CustomerLayout>
  );
};

export default BulkSms;